import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import useToastAlert from "@/hooks/useToastAlert";
import {
  LoadingSpinner,
  PrimaryBlueButton,
  RouteHeader,
  Severity
} from "@velaro/velaro-component-lib";
import AddWidgetModal from "./AddWidgetModal";
import EditWidgetPanel from "./EditWidgetPanel";
import PageWidgetHelp from "./PageWidgetHelp";
import { PremiumTag } from "@/components/PremiumTag";

export interface PageWidget {
  id: number;
  siteId: number;
  widgetId: string;
  name: string;
  widgetType: "click_to_call" | "click_to_text" | "topic_chat";
  settingsJson: string;
  displayRulesJson: string;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

const WIDGET_TYPE_LABELS: Record<string, string> = {
  click_to_call: "Click to Call",
  click_to_text: "Click to Text",
  topic_chat: "Topic Chat"
};

const WIDGET_TYPE_ICONS: Record<string, string> = {
  click_to_call: "fa-phone",
  click_to_text: "fa-comment-sms",
  topic_chat: "fa-comments"
};

type View = "list" | "edit";

export default function PageWidgets() {
  const api = useApi();
  const features = useAdminFeatures();
  const toastAlert = useToastAlert();

  const [widgets, setWidgets] = useState<PageWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [view, setView] = useState<View>("list");
  const [selectedWidget, setSelectedWidget] = useState<PageWidget | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.admin.get("PageWidgets");
      const data = await res.json();
      setWidgets(Array.isArray(data) ? data : []);
    } catch {
      setWidgets([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleToggleEnabled(widget: PageWidget) {
    try {
      await api.admin.put(`PageWidgets/${widget.id}`, {
        isEnabled: !widget.isEnabled
      });
      setWidgets((prev) =>
        prev.map((w) =>
          w.id === widget.id ? { ...w, isEnabled: !w.isEnabled } : w
        )
      );
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to update widget.");
    }
  }

  async function handleDelete(widget: PageWidget) {
    if (!window.confirm(`Delete "${widget.name}"? This cannot be undone.`)) return;
    try {
      await api.admin.delete(`PageWidgets/${widget.id}`);
      setWidgets((prev) => prev.filter((w) => w.id !== widget.id));
      toastAlert.displayToast(Severity.Success, "Widget deleted.");
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to delete widget.");
    }
  }

  function handleEdit(widget: PageWidget) {
    setSelectedWidget(widget);
    setView("edit");
  }

  function handleBackToList() {
    setView("list");
    setSelectedWidget(null);
    load();
  }

  if (!features.enablePageWidgets) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center gap-4">
        <i className="fa-solid fa-puzzle-piece text-4xl text-cornflower-blue-400" />
        <div className="text-xl font-semibold">Page Widgets</div>
        <div className="text-gray-500 max-w-md">
          Page Widgets let you deploy lightweight, focused engagement tools — click-to-call,
          click-to-text, or topic chat — on specific pages using your existing embed script.
        </div>
        <PremiumTag />
        <div className="text-sm text-gray-400">Contact your account manager to enable this feature.</div>
      </div>
    );
  }

  if (view === "edit" && selectedWidget) {
    return (
      <EditWidgetPanel
        widget={selectedWidget}
        onClose={handleBackToList}
        onSaved={handleBackToList}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Header actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            Deploy focused engagement widgets on any page — no extra script needed.
          </span>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="text-cornflower-blue-500 text-sm hover:underline"
          >
            {showHelp ? "Hide help" : "How it works"}
          </button>
        </div>
        <PrimaryBlueButton label="Add Widget" onClick={() => setShowAdd(true)} />
      </div>

      {/* Help panel */}
      {showHelp && (
        <div className="bg-white dark:bg-stone-800 rounded-lg border border-gray-200 p-4">
          <PageWidgetHelp />
        </div>
      )}

      {/* Widget list */}
      {loading ? (
        <LoadingSpinner />
      ) : widgets.length === 0 ? (
        <div className="bg-white dark:bg-stone-800 rounded-lg border border-gray-200">
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
            <i className="fa-solid fa-puzzle-piece text-3xl text-gray-300" />
            <div className="font-medium text-gray-600">No page widgets yet</div>
            <div className="text-sm text-gray-400 max-w-sm">
              Create your first widget to add a click-to-call button, SMS prompt, or
              topic chat to any page on your site.
            </div>
            <PrimaryBlueButton label="Add Your First Widget" onClick={() => setShowAdd(true)} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {widgets.map((widget) => (
            <div key={widget.id} className="bg-white dark:bg-stone-800 rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cornflower-blue-100 flex items-center justify-center">
                  <i className={`fa-solid ${WIDGET_TYPE_ICONS[widget.widgetType] ?? "fa-puzzle-piece"} text-cornflower-blue-600`} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{widget.name}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 rounded px-2 py-0.5">
                      {WIDGET_TYPE_LABELS[widget.widgetType] ?? widget.widgetType}
                    </span>
                    {widget.isEnabled ? (
                      <span className="text-xs bg-green-100 text-green-700 rounded px-2 py-0.5">Active</span>
                    ) : (
                      <span className="text-xs bg-gray-100 text-gray-500 rounded px-2 py-0.5">Disabled</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 font-mono mt-0.5 truncate">
                    ID: {widget.widgetId}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleEnabled(widget)}
                    className={`text-xs px-3 py-1 rounded border transition-colors ${
                      widget.isEnabled
                        ? "border-gray-300 text-gray-600 hover:bg-gray-50"
                        : "border-cornflower-blue-300 text-cornflower-blue-600 hover:bg-cornflower-blue-50"
                    }`}
                  >
                    {widget.isEnabled ? "Disable" : "Enable"}
                  </button>
                  <button
                    onClick={() => handleEdit(widget)}
                    className="text-xs px-3 py-1 rounded border border-cornflower-blue-300 text-cornflower-blue-600 hover:bg-cornflower-blue-50 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(widget)}
                    className="text-xs px-3 py-1 rounded border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                    aria-label={`Delete ${widget.name}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddWidgetModal
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}
    </div>
  );
}
